import type { VintApi } from "@/lib/api/VintApi";
import { AuthManager } from "@/lib/AuthManager";
import { type TextFile, TextProviderImpl } from "@/lib/TextProvider";
import { VintApiImpl } from "@/lib/api/VintApiImpl";
import debug from "debug";
import { BallotRendererImpl } from "@/lib/stimmzettel/BallotFactory/BallotRendererImpl";
import type { BallotRenderer } from "@/lib/stimmzettel/BallotFactory/BallotRenderer";
import { type PdfDocumentManager, PdfDocumentManagerImpl } from "@/lib/pdflib2/PdfDocumentManager";
import { AbschnittCElectionEvaluatorImpl } from "@/lib/abschnittc/impl/AbschnittCElectionEvaluatorImpl";
import type { AbschnittCElectionEvaluator } from "@/lib/abschnittc/AbschnittCElectionEvaluator";
import { GIT_COMMIT_AUTHOR, GIT_REVISION } from "@/lib/git-info";

const logger = debug("container");

logger(import.meta.env);

async function vintApi(): Promise<VintApi> {
	return once("voltToolsIopApi", async () => {
		return new VintApiImpl({
			endpoint: import.meta.env.VITE_API_ENDPOINT,
			webSocketEndpoint: import.meta.env.VITE_WS_ENDPOINT
		});
	});
}

async function authManager() {
	return once("authManager", async () => {
		return new AuthManager({
			api: await vintApi(),
			storageKey: "vint-token"
		});
	});
}

async function textProvider() {
	return once("textProvider", async () => {
		return new TextProviderImpl({
			file: (await import("@/assets/yaml/texts.yaml")).default as TextFile
		});
	});
}

async function gitInfo() {
	return once("gitInfo", async () => {
		return {
			gitRevision: GIT_REVISION,
			gitAuthor: GIT_COMMIT_AUTHOR
		};
	});
}

async function abschnittCElectionEvaluator() {
	return once("abschnittCElectionEvaluator", async () => {

		const thePdfDocumentManager = await pdfDocumentManager();
		const theTextProvider = await textProvider();
		const theGitInfo = await gitInfo();

		return new AbschnittCElectionEvaluatorImpl({
			pdfDocumentManager: thePdfDocumentManager,
			textProvider: theTextProvider,
			gitAuthor: theGitInfo.gitAuthor,
			gitRevision: theGitInfo.gitRevision
		});
	});
}

async function ballotRenderer() {
	return once("ballotRenderer", async () => {

		const thePdfDocumentManager = await pdfDocumentManager();
		const theTextProvider = await textProvider();

		return new BallotRendererImpl({
			pdfDocumentManager: thePdfDocumentManager,
			textProvider: theTextProvider
		});
	});
}

async function pdfDocumentManager() {
	return once("pdfDocumentManager", async () => {
		return new PdfDocumentManagerImpl({
			debug: false
		});
	});
}

const instances: { [name: string]: any } = [];
const instanceInCreation: { [name: string]: ((t: any) => void)[] } = {};

async function once<T>(name: string, creator: () => Promise<T>): Promise<T> {
	return new Promise((resolve) => {
		if (instances.hasOwnProperty(name)) {
			resolve(instances[name]);
		} else {
			if (!instanceInCreation.hasOwnProperty(name)) {
				instanceInCreation[name] = [resolve];
				logger(`Creating ${name}`);
				creator().then((instance) => {

					logger(`Created ${name}`);
					instances[name] = instance;
					instanceInCreation[name].forEach((r) => r(instance));
					delete instanceInCreation[name];
				});
			} else {
				instanceInCreation[name].push(resolve);
			}
		}
	});
}

export interface Container {
	vintApi(): Promise<VintApi>;

	authManager(): Promise<AuthManager>;

	abschnittCElectionEvaluator(): Promise<AbschnittCElectionEvaluator>;

	pdfDocumentManager(): Promise<PdfDocumentManager>;

	ballotRenderer(): Promise<BallotRenderer>;
}

export default {
	vintApi: () => vintApi(),
	authManager: () => authManager(),
	abschnittCElectionEvaluator: () => abschnittCElectionEvaluator(),
	pdfDocumentManager: () => pdfDocumentManager(),
	ballotRenderer: () => ballotRenderer()
} as Container;
